import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {GenericModal} from '@techsee/techsee-ui-common/lib/modals/generic';
import Translate from '../../components/translate/Translate';
import './ScanResultModal.style.scss';
import {ScanResultModalController} from './ScanResultModal.controller';

interface ScanResultModalProps {
    ctrl: ScanResultModalController;
}

@observer
export class ScanResultModal extends TechseeBaseComponent<ScanResultModalProps> {
    private textRef = React.createRef<HTMLPreElement>();

    renderInternal(): JSX.Element {
        const {ctrl} = this.props;

        if (!ctrl) return <div />;

        return (
            <GenericModal
                className={`modal-dialog scan-result-modal ${!ctrl.resultsFound ? 'no-header' : ''}`}
                show={ctrl.isVisible}
                isCloseVisible={false}
                onHideHandler={() => ctrl.close()}
                header={() => this.renderHeader()}
                toolbar={() => null}
                content={() => this.renderContent()}
            />
        );
    }

    private getSelectionText = () => {
        const sel = window.getSelection();
        if (!sel || !sel.toString()) return '';

        const node = sel.anchorNode;
        if (!this.textRef.current || !this.textRef.current.contains(node)) return '';

        return sel.toString();
    };

    private copyPlainValue = async (value: string, id: string) => {
        const {ctrl} = this.props;
        try {
            await navigator.clipboard.writeText(value);
            ctrl.markCopySuccess(id);
        } catch {
            ctrl.markCopyError(id);
        }
    };

    private copyTextBlock = async (id: string) => {
        const {ctrl} = this.props;
        try {
            const selected = this.getSelectionText();
            const normalizedText = selected || this.removeHtml(ctrl.formattedText);

            await navigator.clipboard.writeText(normalizedText);
            ctrl.markCopySuccess(id);
        } catch {
            ctrl.markCopyError(id);
        }
    };

    private removeHtml = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.innerText || div.textContent || '';
    };

    private renderHeader = () => {
        const {ctrl} = this.props;
        if (!ctrl.resultsFound) return null;
        return (
            <>
                <h3>
                    <Translate text='SCAN.RESULT.TITLE' />
                </h3>
                <button className='close' onClick={() => ctrl.close()}>
                    <span aria-hidden='true'>&times;</span>
                </button>
            </>
        );
    };

    private renderResultsList = () => {
        const {ctrl} = this.props;
        if (!ctrl.result) return null;

        return (
            <>
                {Object.entries(ctrl.result).map(([type, results]: any) =>
                    Array.isArray(results) && results.length ? (
                        <div key={type}>
                            <h4 className='margin-bottom-20'>
                                <Translate text={type} />
                            </h4>

                            {results.map((item: any, idx: number) => {
                                const id = `${type}${idx}`;
                                return (
                                    <div className='scan-result-row' key={id}>
                                        {item.type && <label htmlFor={id}>{item.type}:</label>}

                                        <input id={id} type='text' readOnly value={item.value} />

                                        <div className='btn-group pull-right'>
                                            {ctrl.searchTags && (
                                                <button
                                                    className='btn btn-default btn-sm'
                                                    onClick={() => ctrl.triggerSearch(type, idx)}>
                                                    <Translate text='SCAN.RESULT.SEARCH' />
                                                </button>
                                            )}

                                            <button
                                                className='btn btn-default btn-sm'
                                                onClick={() => this.copyPlainValue(item.value, id)}>
                                                <Translate text='SCAN.RESULT.COPY' />
                                            </button>
                                        </div>

                                        <span className='small-text'>
                                            <Translate text={ctrl.copyResults[id] || ''} />
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null
                )}
            </>
        );
    };

    private renderTextResults = () => {
        const {ctrl} = this.props;
        if (!ctrl.displayText) return null;

        return (
            <div className='text-results-message'>
                {/* Don't show "not found" message when displaying text results - the text itself is the result */}

                <pre
                    id='text-scan-result'
                    ref={this.textRef}
                    dir={ctrl.dir}
                    className={ctrl.dir === 'rtl' ? 'text-right' : ''}
                    dangerouslySetInnerHTML={{__html: ctrl.formattedText}}
                />

                <div className='btn-group'>
                    {ctrl.searchTags && (
                        <button className='btn btn-default btn-sm' onClick={() => ctrl.triggerSearch('text')}>
                            <Translate text='SCAN.RESULT.SEARCH' />
                        </button>
                    )}

                    <button className='btn btn-default btn-sm' onClick={() => this.copyTextBlock('text')}>
                        <Translate text='SCAN.RESULT.COPY' />
                    </button>

                    <span className='small-text margin-left-20'>
                        <Translate text={ctrl.copyResults['text'] || ''} />
                    </span>

                    <div className='clearfix'></div>
                </div>
            </div>
        );
    };

    private renderNothingFound = () => {
        const {ctrl} = this.props;
        if (ctrl.resultsFound) return null;

        return (
            <div className='nothing-found-message'>
                <span className='not-found-text'>
                    <Translate text='SCAN.RESULT.NOTHING_FOUND' translateValues={{type: ctrl.translate(ctrl.type)}} />
                </span>
                <button className='close-btn' onClick={() => ctrl.close()}>
                    &times;
                </button>
            </div>
        );
    };

    private renderContent = () => {
        const {ctrl} = this.props;
        return (
            <div className='modal-body text-left' dir={ctrl.dir}>
                {ctrl.result && this.renderResultsList()}
                {this.renderTextResults()}
                {this.renderNothingFound()}
            </div>
        );
    };
}
